import { useContext, useState } from 'react';

import { formatDate, noop } from '@ajaibid/utils';
import { API_SUCCESS_CODE } from '@config/constanta';
import { AlertContext } from '@context/alert';
import useLayout from '@context/layout/useLayout';

import stockService from '@services/stock';
import { postStockBuy, postStockSell, postStockAutoOrder } from '@services/api/stock/trading';
import { postDtBuy, postDtSell } from '@services/api/stock/dayTrading';
import { postMtBuy, postMtSell, postMtAutoOrder } from '@services/api/stock/marginTrading';

import { CONST_DT_CUSTOM_ERROR_CODES } from '../../constants';
import { useEventTracker } from '../../tracker';
import useStockBuySellContext from './useStockBuySellContext';

const makeLimitOrderPayload = ({ code, formState, formKey }) => ({
  ticker_code: code,
  price: formState[formKey.INPUT_PRICE],
  lot: formState[formKey.INPUT_LOT_COUNT],
  board: '0RG',
  period: formState[formKey.INPUT_ORDER_PERIOD]?.value?.toLowerCase(),
});

const makeAutoOrderPayload = ({ code, formState, formKey }) => ({
  code,
  side: formState[formKey.INPUT_ORDER_SIDE],
  order_price: formState[formKey.INPUT_PRICE],
  lot: formState[formKey.INPUT_LOT_COUNT],
  expiry: 'day',
  start_date: formatDate(formState[formKey.INPUT_START_DATE], 'YYYY-MM-DD'),
  end_date: formatDate(formState[formKey.INPUT_END_DATE], 'YYYY-MM-DD'),
  criterion: 'PRICE',
  comparator: formState[formKey.INPUT_THRESHOLD_COMPARATOR]?.value,
  value: formState[formKey.INPUT_THRESHOLD_VALUE],
});

function useSubmitBuySell(code, { onSuccess = noop, onDtError } = {}) {
  const layout = useLayout();
  const { showAlert } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);

  const {
    formState,
    formKey,
    isBuy,
    isSell,
    isAccountRegular,
    isAccountDT,
    isAccountMT,
    isLimitOrder,
    isAutoOrder,
  } = useStockBuySellContext();

  const { isTradeable: isDtTradeable } = stockService.detail.dt.tradeable.useDtTradeable(code);
  const { isTradeable: isMtTradeable } = stockService.detail.mt.tradeable.useMtTradeable(code);
  const { refetch: refetchTradelimit } = stockService.account.regular.tradelimit.useTradelimit();
  const { refetch: refetchDtTradelimit } = stockService.account.dt.tradelimit.useDtTradelimit({
    code,
    enabled: isDtTradeable, // Only call API when stock is DT tradeable
  });
  const { refetch: refetchMtTradelimit } = stockService.account.mt.tradelimit.useMtTradelimit({
    code,
    enabled: isMtTradeable, // Only call API when stock is MT tradeable
  });
  const { refetch: refetchPortfolio } =
    stockService.account.regular.portfolio.usePortfolioByCode(code);
  const { refetch: refetchDtPortfolio } =
    stockService.account.dt.portfolio.useDtPortfolioByCode(code);
  const { refetch: refetchMtPortfolio } =
    stockService.account.mt.portfolio.useMtPortfolioByCode(code);

  const { eventOrderSendFail, eventOrderSendSuccess } = useEventTracker();

  const makeApiCall = async (post, payload, params = {}) => {
    const {
      errorCodeKey = 'err_code',
      errorMessageKey = 'err_message',
      successMessage = 'Berhasil mengirim order',
      defaultErrorMessage = 'Gagal membuat transaksi',
      refetchData = () => {},
    } = params;

    setIsLoading(true);
    const response = await post(payload);

    const isSuccess = response?.[errorCodeKey] === API_SUCCESS_CODE;
    const isDtError = CONST_DT_CUSTOM_ERROR_CODES.includes(response?.[errorCodeKey]);
    const isOutsideMarketHour = response?.result?.status === '10';
    switch (true) {
      case isSuccess:
        // handle dt specific case, success but outside market hour
        // message 'Sedang berada diluar jam perdagangan bursa.'
        if (isAccountDT && isBuy && isOutsideMarketHour) {
          eventOrderSendFail();
          showAlert('Sedang berada diluar jam perdagangan bursa.', 'error');
          break;
        }
        // For success, show success message as an alert
        eventOrderSendSuccess();
        showAlert(successMessage, 'success');
        refetchData();
        onSuccess();
        break;
      case isDtError:
        // For DT-specific error, handle differently
        eventOrderSendFail();
        refetchData();
        onDtError(response?.[errorCodeKey]);
        break;
      default:
        // For other error, show error message as an alert
        eventOrderSendFail();
        showAlert(response?.[errorMessageKey] || defaultErrorMessage, 'error');
        refetchData();
        break;
    }
    setIsLoading(false);

    // Refetch data on order open & order history
    layout.bottomPanel.refetchTable();
  };

  const submitBuySell = () => {
    switch (true) {
      case isLimitOrder && isAccountRegular && isBuy: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postStockBuy, payload, {
          errorCodeKey: 'errCode',
          errorMessageKey: 'statusMessage',
          successMessage: 'Berhasil mengirim order beli',
          refetchData: () => refetchTradelimit(),
        });
        break;
      }
      case isLimitOrder && isAccountRegular && isSell: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postStockSell, payload, {
          errorCodeKey: 'errCode',
          errorMessageKey: 'statusMessage',
          successMessage: 'Berhasil mengirim order jual',
          refetchData: refetchPortfolio,
        });
        // TODO: handle sell bonus here
        break;
      }
      case isLimitOrder && isAccountDT && isBuy: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postDtBuy, payload, {
          successMessage: 'Berhasil mengirim order beli',
          refetchData: refetchDtTradelimit,
        });
        break;
      }
      case isLimitOrder && isAccountDT && isSell: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postDtSell, payload, {
          successMessage: 'Berhasil mengirim order jual',
          refetchData: refetchDtPortfolio,
        });
        break;
      }
      case isLimitOrder && isAccountMT && isBuy: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postMtBuy, payload, {
          successMessage: 'Berhasil mengirim order beli',
          refetchData: refetchMtTradelimit,
        });
        break;
      }
      case isLimitOrder && isAccountMT && isSell: {
        const payload = makeLimitOrderPayload({ code, formState, formKey });
        makeApiCall(postMtSell, payload, {
          successMessage: 'Berhasil mengirim order jual',
          refetchData: refetchMtPortfolio,
        });
        break;
      }
      case isAutoOrder && isAccountRegular: {
        const _autoOrder = (isBuy && 'Auto Order Buy') || (isSell && 'Auto Order Sell') || '';
        const payload = makeAutoOrderPayload({ code, formState, formKey });
        makeApiCall(postStockAutoOrder, payload, {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          successMessage: `${_autoOrder} ${code} telah aktif`,
          defaultErrorMessage: `Gagal mengaktifkan ${_autoOrder} ${code}`,
          refetchData: () => {
            refetchTradelimit();
            refetchPortfolio();
          },
        });
        break;
      }
      case isAutoOrder && isAccountMT: {
        const _autoOrder = (isBuy && 'Auto Order Buy') || (isSell && 'Auto Order Sell') || '';
        const payload = makeAutoOrderPayload({ code, formState, formKey });
        makeApiCall(postMtAutoOrder, payload, {
          errorCodeKey: 'errCode',
          errorMessageKey: 'errMessage',
          successMessage: `${_autoOrder} ${code} telah aktif`,
          defaultErrorMessage: `Gagal mengaktifkan ${_autoOrder} ${code}`,
          refetchData: () => {
            refetchMtTradelimit();
            refetchMtPortfolio();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  return { isLoading, submitBuySell };
}

export default useSubmitBuySell;
